import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { countryDisplayName } from '../../services/countryIso';
import {
    DEFAULT_COUNTRY,
    is_possible_phone,
    parse_phone_field_value,
    phone_country_options,
    serialize_phone_field_value,
} from './phoneFieldValue';

import './PhoneField.css';

const PhoneField = ({ input, required }) => {
    const { t, i18n } = useTranslation();

    const options = useMemo(
        () => phone_country_options(i18n.language, (iso) => countryDisplayName(iso, i18n.language)),
        [i18n.language],
    );

    const parsed = parse_phone_field_value(input.value);
    const country = parsed.country || DEFAULT_COUNTRY;
    const number = parsed.display;

    const set_validity = (el, national_digits) => {
        if (!el)
            return;
        const digits = String(national_digits || '').replace(/\D/g, '');
        if (!digits || is_possible_phone(country, digits))
            el.setCustomValidity('');
        else
            el.setCustomValidity(t('users.invalid_phone'));
    };

    const handle_number = (raw, input_el) => {
        const digits = String(raw || '').replace(/\D/g, '');
        if (digits.length > 15)
            return;
        if (digits === '') {
            input.onChange('');
            set_validity(input_el, '');
            return;
        }
        input.onChange(serialize_phone_field_value(country, digits));
        set_validity(input_el, digits);
    };

    const handle_country = (iso) => {
        input.onChange(serialize_phone_field_value(iso, parsed.national, {
            keep_prefix_when_empty: true,
        }));
    };

    return (
        <div className="PhoneField">
            <select
                className="form-control indicative"
                onChange={(e) => handle_country(e.target.value)}
                value={country}
                title={options.find((o) => o.iso === country)?.title || country}
            >
                {options.map((o) => (
                    <option key={o.iso} value={o.iso} title={o.title}>
                        {o.text}
                    </option>
                ))}
            </select>
            <input
                type="text"
                className="form-control number"
                inputMode="tel"
                autoComplete="tel-national"
                value={number}
                required={!!required}
                onInvalid={(e) => {
                    if (!e.target.validationMessage)
                        e.target.setCustomValidity(t('users.invalid_phone'));
                }}
                onInput={(e) => e.target.setCustomValidity('')}
                onChange={(e) => handle_number(e.target.value, e.target)}
                placeholder={t('users.phone_placeholder')}
            />
        </div>
    );
};

export default PhoneField;
